import {
  gzipRynReplayBatch,
  RynReplayCompressor,
} from '@ups/xplat/base/ryn-replay';

/**
 * Off-main-thread gzip for Ryn Replay batches (#31398 ladder step 3).
 *
 * Capture itself (MutationObserver + DOM serialize) MUST stay on the main
 * thread — workers have no DOM. What CAN move is post-event work: JSON.stringify
 * of an already-built envelope array plus CompressionStream gzip. That is the
 * cost of a dense FullSnapshot flush, and it used to contend with input/paint
 * even after idle scheduling.
 *
 * Implementation is a Blob-URL Worker so no webpack/Nx worker-loader config is
 * required across every web app. Unavailable Worker / CompressionStream falls
 * back to the main-thread compressor; capture must never fail closed because a
 * worker could not start.
 */

const WORKER_SOURCE = `
self.onmessage = async (event) => {
  const data = event.data || {};
  const id = data.id;
  try {
    const CompressionStreamRef = self.CompressionStream;
    if (typeof CompressionStreamRef !== 'function') {
      throw new Error('CompressionStream unavailable in worker');
    }
    const json =
      typeof data.json === 'string'
        ? data.json
        : JSON.stringify(data.envelopes ?? []);
    const stream = new Response(json).body.pipeThrough(
      new CompressionStreamRef('gzip')
    );
    const buffer = await new Response(stream).arrayBuffer();
    self.postMessage({ id: id, ok: true, buffer: buffer }, [buffer]);
  } catch (err) {
    const message =
      err && typeof err === 'object' && 'message' in err
        ? String(err.message)
        : String(err);
    self.postMessage({ id: id, ok: false, error: message });
  }
};
`;

type WorkerResponse =
  | { id: number; ok: true; buffer: ArrayBuffer }
  | { id: number; ok: false; error: string };

/**
 * Build a compressor that stringifies + gzips inside a dedicated Worker when
 * the platform supports it. Returns null when Worker or Blob URLs are missing
 * (SSR / older test hosts), so the caller can fall back.
 *
 * If the worker crashes mid-session, the batches in flight and every later
 * batch are compressed with `fallback` on the main thread instead, so a crash
 * never leaves a flush unsettled.
 */
export function tryCreateWorkerGzipCompressor(
  fallback: RynReplayCompressor = gzipRynReplayBatch
): RynReplayCompressor | null {
  if (
    typeof Worker === 'undefined' ||
    typeof Blob === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return null;
  }

  let worker: Worker;
  let objectUrl: string;
  try {
    const blob = new Blob([WORKER_SOURCE], {
      type: 'application/javascript',
    });
    objectUrl = URL.createObjectURL(blob);
    worker = new Worker(objectUrl);
  } catch {
    return null;
  }

  let nextId = 1;
  const pending = new Map<
    number,
    {
      json: string;
      resolve: (value: Uint8Array) => void;
      reject: (reason: unknown) => void;
    }
  >();
  let crashed = false;

  worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
    const data = event.data;
    const waiter = pending.get(data.id);
    if (!waiter) {
      return;
    }
    pending.delete(data.id);
    if (data.ok === true) {
      waiter.resolve(new Uint8Array(data.buffer));
      return;
    }
    waiter.reject(new Error(data.error || 'Ryn Replay gzip worker failed'));
  };

  worker.onerror = () => {
    crashed = true;
    worker.terminate();
    URL.revokeObjectURL(objectUrl);
    const inFlight = [...pending.values()];
    pending.clear();
    for (const waiter of inFlight) {
      fallback(waiter.json).then(waiter.resolve, waiter.reject);
    }
  };

  return (json: string) => {
    if (crashed) {
      return fallback(json);
    }
    return new Promise<Uint8Array>((resolve, reject) => {
      const id = nextId++;
      pending.set(id, { json, resolve, reject });
      try {
        worker.postMessage({ id, json });
      } catch (err) {
        pending.delete(id);
        reject(err);
      }
    });
  };
}

/**
 * Prefer the worker compressor; fall back to main-thread gzip so ingest still
 * receives valid bodies when Worker construction fails.
 */
export function createRynReplayGzipCompressor(): RynReplayCompressor {
  return tryCreateWorkerGzipCompressor() ?? gzipRynReplayBatch;
}
